import type { BootFailedError } from "./types";
import {
  type BootDiagnostics,
  type BootApplicationParams,
  type InlineFrameLoadResult,
  ErrorType,
  KpiMetricName,
  OwningFeatureCrewAlias,
  QosErrorName,
  QosVetoName,
} from "./types";
import { formId, frameId, initFrameRequest } from "./frame";
import {
  createAdaptiveLogger,
  createNewGuid,
  getAbsoluteTimeSinceEpoch,
  LogCategory,
  TraceLevel,
} from "@1js/office-online-jsapi-core";
import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import { createAndAppendOwletEventHandlers } from "./events/createOwletEventHandlers";
import { getDriveItemWithShareLink } from "./vroom/getDriveItemWithShareLink";
import { updateLoggerDataBoundaryUsingRegion } from "./frame/updateLoggerWithDataBoundary";
import {
  sendMessage,
  sendMessageAwaitResponse,
} from "./messages/channelMessageUtility";
import { Health, KpiType } from "@1js/office-online-qos-small";
import type {
  ICommandMessage,
  IMessage,
} from "./messages/messageTypes/Message";
import type { IWACSessionEndedNotification } from "./messages/messageTypes/Notification";
import type {
  IWACHostAccessibilityLoopCompleteCommand,
  IWACHostEndDocumentSession,
} from "./messages/messageTypes/Command";
import { FileType } from "@1js/owl-shared-types";

let logger: AdaptiveLogger;
let health: Health;
/**
 * Entry point to boot application from owl enhancement strategy(OWLET)
 * TODO: Add telemetry(BULS and Health), task id 8782423
 */
export async function bootApplication(
  bootAppParams: BootApplicationParams
): Promise<BootDiagnostics> {
  const packageEntryTime = getAbsoluteTimeSinceEpoch();
  const bootDiagnostics: { [key: string]: any } = {};
  bootDiagnostics.packageEntryTime = packageEntryTime;
  const {
    documentBootInfo,
    sessionConfig,
    uiHostConfig,
    getAuthToken,
    eventHandlers: uiHostEventHandlers,
    container,
  } = bootAppParams;

  const hostCorrelationId = sessionConfig.hostCorrelationId;
  bootDiagnostics.hostCorrelationId = hostCorrelationId;

  sessionConfig.bootDiagnostics = sessionConfig.bootDiagnostics || {};
  sessionConfig.bootDiagnostics.owletBootApplicationStartTime =
    packageEntryTime;

  // Initialize BULS logger with sessionId only to prevent log flushing till obtained data boundary
  logger = createAdaptiveLogger({
    sessionId: hostCorrelationId,
  });

  // wac session Id consumed for logging. NOTE: OWL session ID = WAC session ID
  const sessionId = createNewGuid()[0];
  logger.ULS.sendTraceTag(
    0x1e35545e /* tag_4nvr4 */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    `BootApplication: WacSessionId created: ${sessionId}, binding with hostCorrelationId: ${hostCorrelationId}.`
  );

  health = Health.getInstance();
  health?.recordKpiUsage(
    KpiMetricName.KPI_OwletBoot,
    KpiType.FailureBased as number,
    OwningFeatureCrewAlias.FC_Owl,
    new Map<string, string>([
      ["DocumentBootStartTime", new Date().toUTCString()],
      ["Scenario", `OwletBootApplication`],
      ["SessionId", sessionId],
    ])
  );

  // Appends owlet event handlers onto uiHostEventHandlers and returns all handlers
  const eventHandlers = createAndAppendOwletEventHandlers(
    documentBootInfo,
    sessionConfig,
    uiHostConfig,
    getAuthToken,
    container,
    uiHostEventHandlers,
    documentBootInfo.hostingPageOrigin,
    logger,
    health,
    sessionId
  );

  // create inline frame
  let vroomAccessToken: string | undefined;
  const shareUrl = documentBootInfo.shareUrl;

  // retrieve document metadata with shareUrl through SPO vroom call
  if (!!shareUrl) {
    logger.ULS.sendTraceTag(
      0x1e2d91a1 /* tag_4lzg7 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Important,
      `BootApplication: Fetch drive item to support document booting via a share URL.`
    );
    sessionConfig.bootDiagnostics.owletGetDriveItemStartTime =
      getAbsoluteTimeSinceEpoch();

    try {
      const shareUrlBootDocInfo = await getDriveItemWithShareLink(
        shareUrl,
        getAuthToken,
        logger
      );
      documentBootInfo.docTenantUrl = shareUrlBootDocInfo.siteUrl;
      documentBootInfo.sourceDoc = shareUrlBootDocInfo.listItemUniqueId;
      // Only assign the vroom returned appName to fileType when the fileType in bootDocumentInfo does not already exist and vroom returned a valid appName.
      if (
        !documentBootInfo.fileType &&
        shareUrlBootDocInfo.appName &&
        Object.values(FileType).includes(shareUrlBootDocInfo.appName)
      ) {
        documentBootInfo.fileType = shareUrlBootDocInfo.appName as FileType;
      }
      vroomAccessToken = shareUrlBootDocInfo.vroomAccessToken;
      sessionConfig.bootDiagnostics.owletGetDriveItemEndTime =
        getAbsoluteTimeSinceEpoch();
    } catch (error) {
      logger.ULS.sendTraceTag(
        0x1e318294 /* tag_4myku */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `BootApplication: Failed to boot Doc.aspx iframe with shareUrl: ${error}`
      );

      health?.recordQosError(
        QosErrorName.QosError_OwletVroomGetDriveItemFailure,
        [QosVetoName.QosVeto_GetDriveItemFailed],
        false /* isIntentional */,
        false /* isInternal */,
        false /* alertOnPillar */,
        false /* isSessionEndingError */,
        {
          extendedProperties: new Map<string, string>([
            ["ErrorName", QosErrorName.QosError_OwletVroomGetDriveItemFailure],
            ["CorrelationId", `${sessionConfig.hostCorrelationId}`],
            [
              "Reason",
              `OWLET failed to fetch driveItem when boot document with shareUrl.`,
            ],
          ]),
        }
      );

      bootDiagnostics.isBootSuccess = false;
      bootDiagnostics.errorInfo = {
        errorMessage: `Failed to fetch document metadata by vroom request.`,
        errorName: "FailedGetDriveItem",
        errorType: ErrorType.HttpError,
      } as BootFailedError;
      bootDiagnostics.bootApplicationFinishedTime = getAbsoluteTimeSinceEpoch();
      // Flush BULS and Health to common endpoint if the session fails before SPO frame creation.
      updateLoggerDataBoundaryUsingRegion(
        "" /* region */,
        sessionConfig.wiseEnvironment,
        logger,
        health,
        sessionId,
        documentBootInfo,
        sessionConfig,
        uiHostConfig
      );
      return Promise.resolve(bootDiagnostics as BootDiagnostics);
    }
  }

  // create inline frame with docTenantUrl and sourceDoc
  bootDiagnostics.bootApplicationStartTime = getAbsoluteTimeSinceEpoch();
  return initFrameRequest(
    documentBootInfo,
    sessionConfig,
    uiHostConfig,
    getAuthToken,
    container,
    sessionId,
    eventHandlers,
    vroomAccessToken, // auth token provide to SPO when initiate doc.aspx frame with shareUrl
    logger,
    health
  )
    .then((loadResult: InlineFrameLoadResult) => {
      bootDiagnostics.isBootSuccess = loadResult.isLoaded;
      //trigger onBootComplete handler
      if (eventHandlers.onBootComplete) {
        // TODO: feed wacBootDiagnostics as input after SPO send it via post msg
        eventHandlers.onBootComplete(documentBootInfo);
      }
      bootDiagnostics.bootApplicationFinishedTime = getAbsoluteTimeSinceEpoch();
      return bootDiagnostics as BootDiagnostics;
    })
    .catch((loadError) => {
      logger.ULS.sendTraceTag(
        0x1e35545d /* tag_4nvr3 */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `BootApplication: Failed to boot Doc.aspx iframe due to error: ${loadError.error}`
      );

      health?.recordQosError(
        QosErrorName.QosError_OwletBootApplicationFailure,
        [QosVetoName.QosVeto_BootDocumentExperienceFailed],
        false /* isIntentional */,
        false /* isInternal */,
        false /* alertOnPillar */,
        false /* isSessionEndingError */,
        {
          extendedProperties: new Map<string, string>([
            ["ErrorName", QosErrorName.QosError_OwletBootApplicationFailure],
            ["CorrelationId", `${sessionConfig.hostCorrelationId}`],
            ["Reason", `OWLET failed to boot document.`],
          ]),
        }
      );

      bootDiagnostics.isBootSuccess = loadError.isLoaded;
      // call onBootFailure handler, boot failure including timeout/error
      if (eventHandlers.onBootFailure) {
        eventHandlers.onBootFailure(loadError.error, documentBootInfo);
      }
      bootDiagnostics.errorInfo = loadError.error;
      bootDiagnostics.bootApplicationFinishedTime = getAbsoluteTimeSinceEpoch();
      // Flush BULS and Health to common endpoint if the session fails before/during SPO frame creation.
      updateLoggerDataBoundaryUsingRegion(
        "" /* region */,
        sessionConfig.wiseEnvironment,
        logger,
        health,
        sessionId,
        documentBootInfo,
        sessionConfig,
        uiHostConfig
      );
      return bootDiagnostics as BootDiagnostics;
    });
}

/**
 * Initiate force end session from UI Host
 */
export async function endSessionFromHost(): Promise<string> {
  const message: ICommandMessage = {
    type: "command",
    id: 0,
    data: {
      command: "host-end-document-session",
    } as IWACHostEndDocumentSession,
  };

  const expectedResponse = "document-unloaded";

  function response(event: IWACSessionEndedNotification) {
    return event.notification;
  }

  function timeout(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms)).then(() => {
      return "End Session timed out, disposing frame without waiting for response.";
    });
  }

  return await Promise.race([
    sendMessageAwaitResponse(message, expectedResponse, response),
    timeout(2000),
  ])
    .then((response) => {
      console.log(`SessionEnded: ${response}`);
      logger?.ULS.sendTraceTag(
        0x1e2d9588 /* tag_4lzwi */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Info,
        `SessionEnded: End session executed successfully with response: ${response}`
      );
      return response;
    })
    .catch((error) => {
      console.log(`SessionEnded: ${error}`);
      logger?.ULS.sendTraceTag(
        0x1e2c608a /* tag_4lgck */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `SessionEnded: ${error}`
      );
      return error;
    })
    .finally(() => {
      // remove frame and form until none exist (there's a chance there could be multiple frames/forms)
      let docFrameExists = true;
      let docFormExists = true;
      while (docFrameExists || docFormExists) {
        const docFrame = document.getElementById(frameId);
        docFrame ? docFrame.remove() : (docFrameExists = false);
        const docForm = document.getElementById(formId);
        docForm ? docForm.remove() : (docFormExists = false);
      }
    });
}

/**
 * Initiate notification from UI Host, indicates keyboard focus goes from UIHost frame to WAC frame
 */
export function notifyHostAccessibilityLoopComplete(
  navigateForward: boolean
): boolean {
  const message: IMessage = {
    type: "command",
    id: 0,
    data: {
      command: "host-accessibility-loop-complete",
      navigateForward,
    } as IWACHostAccessibilityLoopCompleteCommand,
  };
  sendMessage(message);
  return true;
}
